/**
 * Voice command queue — ordering, mutation serialization, abort, stale rejection (V3-PROVIDER).
 * Dependency-free: no React or DOM.
 */

import type { VoiceCommandRequest, VoiceCommandResponse } from './types';

export type QueuedCommandStatus = 'queued' | 'active' | 'completed' | 'rejected' | 'aborted';

export interface QueuedCommand {
  commandId: string;
  isMutation: boolean;
  status: QueuedCommandStatus;
  abortController: AbortController;
  enqueuedAt: number;
}

export interface EnqueueResult {
  accepted: boolean;
  reason?: 'mutation_in_flight' | 'duplicate';
  entry: QueuedCommand;
}

export interface VoiceCommandQueueOptions {
  /** Max concurrent non-mutation requests (default 3). */
  maxConcurrentRead?: number;
}

/**
 * Serializes mutation commands and caps concurrent read/navigation requests.
 */
export class VoiceCommandQueue {
  private readonly maxConcurrentRead: number;
  private readonly entries = new Map<string, QueuedCommand>();
  private activeMutationId: string | null = null;
  private activeReadCount = 0;

  constructor(options: VoiceCommandQueueOptions = {}) {
    this.maxConcurrentRead = options.maxConcurrentRead ?? 3;
  }

  enqueue(commandId: string, isMutation: boolean): EnqueueResult {
    const existing = this.entries.get(commandId);
    if (existing && existing.status !== 'completed' && existing.status !== 'aborted') {
      return { accepted: false, reason: 'duplicate', entry: existing };
    }

    if (isMutation) {
      for (const entry of this.entries.values()) {
        if (
          entry.isMutation &&
          entry.commandId !== commandId &&
          (entry.status === 'queued' || entry.status === 'active')
        ) {
          const rejected: QueuedCommand = {
            commandId,
            isMutation: true,
            status: 'rejected',
            abortController: new AbortController(),
            enqueuedAt: Date.now(),
          };
          this.entries.set(commandId, rejected);
          return { accepted: false, reason: 'mutation_in_flight', entry: rejected };
        }
      }
    }

    const entry: QueuedCommand = {
      commandId,
      isMutation,
      status: 'queued',
      abortController: new AbortController(),
      enqueuedAt: Date.now(),
    };
    this.entries.set(commandId, entry);
    return { accepted: true, entry };
  }

  markActive(commandId: string): boolean {
    const entry = this.entries.get(commandId);
    if (!entry || entry.status === 'rejected') return false;

    if (entry.isMutation) {
      if (this.activeMutationId && this.activeMutationId !== commandId) return false;
      this.activeMutationId = commandId;
    } else {
      if (this.activeReadCount >= this.maxConcurrentRead) return false;
      this.activeReadCount += 1;
    }

    entry.status = 'active';
    return true;
  }

  complete(commandId: string): void {
    const entry = this.entries.get(commandId);
    if (!entry) return;
    if (entry.isMutation && this.activeMutationId === commandId) {
      this.activeMutationId = null;
    } else if (!entry.isMutation && entry.status === 'active') {
      this.activeReadCount = Math.max(0, this.activeReadCount - 1);
    }
    entry.status = 'completed';
  }

  abort(commandId: string): void {
    const entry = this.entries.get(commandId);
    if (!entry) return;
    entry.abortController.abort();
    if (entry.isMutation && this.activeMutationId === commandId) {
      this.activeMutationId = null;
    } else if (!entry.isMutation && entry.status === 'active') {
      this.activeReadCount = Math.max(0, this.activeReadCount - 1);
    }
    entry.status = 'aborted';
  }

  abortAllExcept(activeUiSessionId: string, currentUiSessionId: string): void {
    if (activeUiSessionId === currentUiSessionId) return;
    for (const [id, entry] of this.entries.entries()) {
      if (entry.status === 'active' || entry.status === 'queued') {
        this.abort(id);
      }
    }
  }

  reset(): void {
    for (const id of this.entries.keys()) {
      this.abort(id);
    }
    this.entries.clear();
    this.activeMutationId = null;
    this.activeReadCount = 0;
  }

  getEntry(commandId: string): QueuedCommand | undefined {
    return this.entries.get(commandId);
  }

  hasActiveMutation(): boolean {
    return this.activeMutationId != null;
  }
}

/** Returns true when a response belongs to an inactive UI session. */
export function isStaleUiSessionResponse(
  responseUiSessionId: string | undefined,
  activeUiSessionId: string | null
): boolean {
  if (!responseUiSessionId || !activeUiSessionId) return false;
  return responseUiSessionId !== activeUiSessionId;
}

/** Guard for applying command results to UI state. */
export function shouldApplyCommandResponse(
  request: Pick<VoiceCommandRequest, 'ui_session_id' | 'command_id'>,
  activeUiSessionId: string | null,
  activeCommandId: string | null
): boolean {
  if (isStaleUiSessionResponse(request.ui_session_id, activeUiSessionId)) return false;
  if (activeCommandId && request.command_id !== activeCommandId) return false;
  return true;
}

export type ApplyResponseDecision = {
  apply: boolean;
  speak: boolean;
  navigate: boolean;
};

export function decideResponseSideEffects(
  response: VoiceCommandResponse,
  request: Pick<VoiceCommandRequest, 'ui_session_id'>,
  activeUiSessionId: string | null
): ApplyResponseDecision {
  const stale = isStaleUiSessionResponse(request.ui_session_id, activeUiSessionId);
  return {
    apply: !stale,
    speak: !stale && Boolean(response.message),
    navigate: !stale && response.action === 'navigate',
  };
}
